/**
 * Environment configuration for FaceWise frontend
 * Centralizes environment variable access with type safety
 */

export function resolveViteApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL as string | undefined
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  const looksUnset =
    !trimmed ||
    trimmed.includes('${') ||
    (!trimmed.startsWith('http://') && !trimmed.startsWith('https://'))
  if (looksUnset) {
    if (import.meta.env.DEV) {
      console.warn(
        '[FaceWise] VITE_API_BASE_URL missing or invalid; using http://localhost:8000/api. In CI, set CodeBuild project env to full URL (https://…/api). Do not set buildspec env.variables to VITE_API_BASE_URL: ${VITE_API_BASE_URL} — CodeBuild treats that as a literal string.'
      )
      return 'http://localhost:8000/api'
    }
    throw new Error(
      'Invalid VITE_API_BASE_URL in production build. It must be an absolute URL (https://your-api-host/api). Common cause: buildspec env.variables contains VITE_API_BASE_URL: ${VITE_API_BASE_URL}, which overrides the CodeBuild project value with a literal placeholder. Remove that line from the buildspec so the project environment variable is used.'
    )
  }
  return trimmed.replace(/\/+$/, '')
}

const resolvedApiBaseUrl = resolveViteApiBaseUrl()
const resolvedApiBaseUrlV1 = resolvedApiBaseUrl.replace(/\/api\/?$/, '/api/v1')

export const ENV = {
  API_BASE_URL: resolvedApiBaseUrl,
  API_BASE_URL_V1: resolvedApiBaseUrlV1,
  USE_VERSIONED_API: import.meta.env.VITE_USE_VERSIONED_API === 'true',
  
  // Environment
  NODE_ENV: import.meta.env.MODE,
  IS_DEVELOPMENT: import.meta.env.DEV,
  IS_PRODUCTION: import.meta.env.PROD,
  
  // Build info
  BUILD_TIME: import.meta.env.BUILD_TIME,
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
} as const

// Type for environment configuration
export type Environment = typeof ENV

/**
 * Infer a short environment name when VITE_APP_ENV isn't set explicitly.
 * Local `vite` dev is always 'dev'; deployed builds are inferred from the
 * API host (both dev and prod are production builds, so import.meta.env.DEV
 * can't tell them apart on its own).
 */
function inferEnvName(): string {
  if (import.meta.env.DEV) return 'dev'
  try {
    const host = new URL(resolvedApiBaseUrl).hostname.toLowerCase()
    if (host.includes('localhost') || host.includes('127.0.0.1')) return 'dev'
    if (host.includes('staging') || host.includes('stage')) return 'staging'
    if (host.includes('dev')) return 'dev'
  } catch {
    /* non-URL base — fall through to prod */
  }
  return 'prod'
}

const explicitEnvName = (import.meta.env.VITE_APP_ENV as string | undefined)
  ?.trim()
  .toLowerCase()

/** Short environment label, e.g. 'dev' / 'staging' / 'prod'. */
export const ENV_NAME = explicitEnvName || inferEnvName()

/** True for any non-production environment — drives the subtle env badge. */
export const IS_NON_PROD = ENV_NAME !== 'prod' && ENV_NAME !== 'production'

// Helper functions
export const isDevelopment = (): boolean => ENV.IS_DEVELOPMENT
export const isProduction = (): boolean => ENV.IS_PRODUCTION

// Get the correct API base URL based on versioning preference
export const getApiBaseUrl = (): string => {
  return ENV.USE_VERSIONED_API ? ENV.API_BASE_URL_V1 : ENV.API_BASE_URL
}






